// Upload dialog store — открытие UploadDialog из любого места (Sidebar, BottomNav, кнопки).
// Не персистится: при перезагрузке всегда закрыт.
//
// `pendingFiles` — канал передачи файлов, брошенных МИМО диалога (drag-n-drop
// над экраном курса, декрет E27). Второй `<UploadDialog/>` монтировать нельзя
// (`course-bridge-toast` — модуль-синглтон с leader-election), поэтому файлы
// кладутся сюда, а конвейер стартует уже внутри единственного глобального
// диалога (`use-upload-dialog.ts`). Забираются РОВНО ОДИН РАЗ
// (`consumePendingFiles`) — иначе повторный монтаж поднял бы загрузку заново.
import { create } from "zustand";

/** Стабильная пустая ссылка — чтобы `consumePendingFiles()` не плодил массивы. */
const NO_FILES: readonly File[] = [];

/**
 * Вкладка диалога загрузки. ЕДИНСТВЕННОЕ объявление на приложение:
 * `use-upload-dialog.ts` реэкспортит его как `UploadTab`, поэтому список
 * вкладок нельзя расширить в одном месте и забыть в другом.
 */
export type UploadDialogTab = "file" | "link" | "text";

export interface UploadDialogOpenOptions {
	/** Папка/коллекция — поле назначения зафиксировано. */
	targetSetId?: string;
	/** Курс — поле курса свёрнуто. */
	targetCourseId?: string;
	/** Предвыбрать папку без блокировки (Библиотека: текущая папка). */
	initialFolderId?: string;
	/**
	 * Вкладка, на которой открыть диалог. Нужна тем поверхностям, где выбор
	 * «Файл / Ссылка / Текст» сделан ДО открытия (карточка загрузки шага 0):
	 * без неё человек жал «Ссылка», а диалог открывался на «Файле».
	 * Не задана — прежнее поведение (диалог сам выбирает «Файл»).
	 */
	initialTab?: UploadDialogTab;
	/** Файлы из внешнего drop — конвейер стартует сразу после открытия. */
	files?: readonly File[];
}

interface UploadDialogStore {
	isOpen: boolean;
	targetSetId: string | null;
	targetCourseId: string | null;
	initialFolderId: string | null;
	/** Вкладка, запрошенная открывающей поверхностью. `null` — на усмотрение диалога. */
	initialTab: UploadDialogTab | null;
	/** Файлы, ожидающие старта конвейера. `null` — забирать нечего. */
	pendingFiles: readonly File[] | null;
	open: (opts?: UploadDialogOpenOptions) => void;
	close: () => void;
	setOpen: (open: boolean) => void;
	/** Досыпать файлы в УЖЕ открытый диалог, не трогая выбранное назначение. */
	setPendingFiles: (files: readonly File[]) => void;
	/** Забрать файлы и очистить очередь (идемпотентность старта загрузки). */
	consumePendingFiles: () => readonly File[];
}

export const useUploadDialogStore = create<UploadDialogStore>((set, get) => ({
	isOpen: false,
	targetSetId: null,
	targetCourseId: null,
	initialFolderId: null,
	initialTab: null,
	pendingFiles: null,
	open: (opts) =>
		set({
			isOpen: true,
			targetSetId: opts?.targetSetId ?? null,
			targetCourseId: opts?.targetCourseId ?? null,
			initialFolderId: opts?.initialFolderId ?? null,
			initialTab: opts?.initialTab ?? null,
			pendingFiles: opts?.files && opts.files.length > 0 ? opts.files : null,
		}),
	close: () =>
		set({
			isOpen: false,
			targetSetId: null,
			targetCourseId: null,
			initialFolderId: null,
			initialTab: null,
			pendingFiles: null,
		}),
	setOpen: (open) =>
		set(
			open
				? { isOpen: true }
				: {
						isOpen: false,
						targetSetId: null,
						targetCourseId: null,
						initialFolderId: null,
						initialTab: null,
						pendingFiles: null,
					},
		),
	setPendingFiles: (files) => set({ pendingFiles: files.length > 0 ? files : null }),
	consumePendingFiles: () => {
		const files = get().pendingFiles;
		if (files === null) return NO_FILES;
		set({ pendingFiles: null });
		return files;
	},
}));
